import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { SECTIONS } from '../design/sections'

/**
 * Top navigation.
 *
 *   Desktop (md+): horizontal inline nav with section numbers + labels.
 *   Mobile (<md):  small "menu" text trigger; opening it reveals a
 *                  fullscreen overlay with the sections as large
 *                  display-typography links.
 *
 * The mobile pattern keeps the wordmark visible in both states, uses no
 * iconography (just typography), and locks body scroll while the overlay
 * is open. The overlay closes automatically on route change.
 */
export function Nav() {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()

  // Auto-close on route change so the overlay doesn't stick after a tap.
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  // Lock body scroll while the overlay is open.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-40 flex items-center justify-between px-6 py-4 mix-blend-difference md:px-10">
        <Link
          to="/"
          className="font-mono text-xs uppercase tracking-[0.2em] text-[color:var(--color-fg)]"
        >
          alatzidis
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 font-mono text-xs uppercase tracking-[0.2em] md:flex">
          {SECTIONS.map((s) => (
            <NavLink
              key={s.id}
              to={s.href}
              className={({ isActive }) =>
                [
                  'transition-opacity',
                  isActive ? 'opacity-100' : 'opacity-60 hover:opacity-100',
                ].join(' ')
              }
            >
              <span>{s.number}—</span>
              {s.label}
            </NavLink>
          ))}
        </nav>

        {/* Mobile trigger */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="font-mono text-xs uppercase tracking-[0.2em] text-[color:var(--color-fg)] md:hidden"
          aria-label="Open menu"
        >
          menu
        </button>
      </header>

      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-[color:var(--color-bg)] md:hidden"
          data-section="home"
        >
          <header className="flex items-center justify-between px-6 py-4">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-[color:var(--color-fg)]"
            >
              alatzidis
            </Link>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-[color:var(--color-fg)]"
              aria-label="Close menu"
            >
              close
            </button>
          </header>

          <nav className="flex flex-1 flex-col justify-center gap-3 px-6 pb-20">
            {SECTIONS.map((s) => (
              <NavLink
                key={s.id}
                to={s.href}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    'group block py-2 transition-colors',
                    isActive
                      ? 'text-[color:var(--color-fg)]'
                      : 'text-[color:var(--color-fg)]/60 hover:text-[color:var(--color-fg)]',
                  ].join(' ')
                }
              >
                <span className="mr-3 font-mono text-xs uppercase tracking-[0.2em] text-[color:var(--color-muted)]">
                  {s.number}
                </span>
                <span className="font-[var(--font-display)] text-4xl font-medium tracking-tight">
                  {s.label}
                </span>
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </>
  )
}
